import * as THREE from 'three';

const cache = {};

let seed = 7;
const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };

const shade = (hex, f) => {
  const c = new THREE.Color(hex);
  c.offsetHSL(0, 0, f);
  return `#${c.getHexString()}`;
};

const noise = (ctx, S, base, amp, n = 9000, size = 2) => {
  ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
  for (let i = 0; i < n; i += 1) {
    ctx.fillStyle = shade(base, (rnd() - 0.5) * amp);
    ctx.fillRect(rnd() * S, rnd() * S, size, size);
  }
};

const DRAW = {
  brick: (ctx, S) => {
    ctx.fillStyle = '#b9ada0'; ctx.fillRect(0, 0, S, S);
    const bw = 64, bh = 21, m = 3;
    for (let r = 0; r * bh < S; r += 1) {
      for (let c = -1; c * bw < S; c += 1) {
        const x = c * bw + (r % 2 ? bw / 2 : 0);
        ctx.fillStyle = shade('#8a4a32', (rnd() - 0.5) * 0.12);
        ctx.fillRect(x + m / 2, r * bh + m / 2, bw - m, bh - m);
      }
    }
  },
  plaster: (ctx, S) => noise(ctx, S, '#e6e0d6', 0.05, 14000, 2),
  concrete: (ctx, S) => {
    noise(ctx, S, '#a7a49f', 0.07, 12000, 2);
    ctx.strokeStyle = 'rgba(0,0,0,.12)'; ctx.lineWidth = 2;
    for (let i = 0; i <= 2; i += 1) { ctx.beginPath(); ctx.moveTo(0, (i * S) / 2); ctx.lineTo(S, (i * S) / 2); ctx.stroke(); }
    for (let i = 0; i <= 1; i += 1) { ctx.beginPath(); ctx.moveTo((i * S) / 1, 0); ctx.lineTo((i * S) / 1, S); ctx.stroke(); }
  },
  wood: (ctx, S) => {
    const n = 8;
    for (let i = 0; i < n; i += 1) {
      ctx.fillStyle = shade('#6b4a33', (rnd() - 0.5) * 0.08);
      ctx.fillRect((i * S) / n, 0, S / n, S);
      ctx.fillStyle = 'rgba(0,0,0,.35)'; ctx.fillRect((i * S) / n, 0, 3, S);
    }
  },
  panel: (ctx, S) => {
    noise(ctx, S, '#cfcac2', 0.03, 4000, 2);
    ctx.fillStyle = 'rgba(0,0,0,.18)';
    for (let i = 0; i < 4; i += 1) ctx.fillRect((i * S) / 4, 0, 3, S);
    ctx.fillRect(0, S / 2, S, 3);
  },
  metal: (ctx, S) => {
    for (let i = 0; i < 16; i += 1) {
      ctx.fillStyle = i % 2 ? '#5d6268' : '#858a90'; ctx.fillRect((i * S) / 16, 0, S / 16, S);
    }
  },
  roof: (ctx, S) => noise(ctx, S, '#3a3836', 0.06, 16000, 2),
  tiles: (ctx, S) => {
    ctx.fillStyle = '#2a2928'; ctx.fillRect(0, 0, S, S);
    for (let r = 0; r < 12; r += 1) {
      const y = r * (S / 12);
      const g = ctx.createLinearGradient(0, y, 0, y + S / 12);
      g.addColorStop(0, '#4a4746'); g.addColorStop(0.85, '#3a3837'); g.addColorStop(1, '#232221');
      ctx.fillStyle = g; ctx.fillRect(0, y, S, S / 12 - 2);
      for (let c = 0; c < 8; c += 1) { ctx.fillStyle = 'rgba(0,0,0,.25)'; ctx.fillRect(c * (S / 8) + (r % 2 ? S / 16 : 0), y, 2, S / 12); }
    }
  },
  asphalt: (ctx, S) => noise(ctx, S, '#2d2c2b', 0.08, 22000, 2),
  grass: (ctx, S) => noise(ctx, S, '#33402b', 0.1, 26000, 3),
  paving: (ctx, S) => {
    noise(ctx, S, '#8f8a83', 0.06, 6000, 2);
    ctx.strokeStyle = 'rgba(0,0,0,.2)'; ctx.lineWidth = 2;
    for (let i = 0; i <= 4; i += 1) {
      ctx.beginPath(); ctx.moveTo(0, (i * S) / 4); ctx.lineTo(S, (i * S) / 4); ctx.stroke();
      ctx.beginPath(); ctx.moveTo((i * S) / 4, 0); ctx.lineTo((i * S) / 4, S); ctx.stroke();
    }
  },
};

// Meter per texturrepetition
export const TEX_SCALE = { brick: 2.2, plaster: 4, concrete: 3, wood: 2.4, panel: 4.8, metal: 3, roof: 6, tiles: 5, asphalt: 8, grass: 10, paving: 4 };

const base = (kind) => {
  if (!cache[kind]) {
    const S = 512;
    const c = document.createElement('canvas');
    c.width = S; c.height = S;
    DRAW[kind](c.getContext('2d'), S);
    const t = new THREE.CanvasTexture(c);
    t.wrapS = THREE.RepeatWrapping; t.wrapT = THREE.RepeatWrapping;
    t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8;
    cache[kind] = t;
  }
  return cache[kind];
};

export const tex = (kind, w, h) => {
  const key = `${kind}:${w.toFixed(1)}:${h.toFixed(1)}`;
  if (!cache[key]) {
    const t = base(kind).clone();
    const s = TEX_SCALE[kind] || 4;
    t.repeat.set(Math.max(w / s, 0.2), Math.max(h / s, 0.2));
    t.needsUpdate = true;
    cache[key] = t;
  }
  return cache[key];
};
